import { explicitWorkspaceHref, parseWorkspaceRoute } from './workspaceRoutes'

// ---------------------------------------------------------------------------
// Where picking a workspace in the navbar switcher takes you.
//
// The URL carries the workspace, so switching always navigates to the
// equivalent surface in the workspace you picked: work surfaces go to its
// project list or library, admin surfaces to its settings — nothing
// half-changes. Pure so the switcher can both navigate to and PREFETCH the
// same href (ENG-723), and so the routing table is unit-testable.
// ---------------------------------------------------------------------------

/** The 'personal' sentinel used by the workspace context store for "no team". */
export const PERSONAL_WORKSPACE_ID = 'personal'

/**
 * Destination for picking workspace `workspaceId` while viewing `pathname`.
 *
 * `teamSlug` is the picked team's slug (ignored for personal). A team without a
 * slug has no addressable surface yet, so it falls back to explicit Personal.
 */
export function workspaceSwitchHref(
  pathname: string | null | undefined,
  workspaceId: string,
  teamSlug: string | null | undefined,
): string {
  const path = pathname ?? ''
  const isPersonal = workspaceId === PERSONAL_WORKSPACE_ID

  const workspaceRoute = parseWorkspaceRoute(path)
  // /team/activate and /team/cancel are public plan-action pages, not
  // team-admin surfaces — switching workspace there goes to its project list.
  const onTeamActionPage = path === '/team/activate' || path === '/team/cancel'
  const onAdminSurface =
    (!workspaceRoute && !onTeamActionPage && path.startsWith('/team/')) ||
    path === '/settings' ||
    path.startsWith('/settings/')

  if (onAdminSurface) {
    if (isPersonal) return '/settings/account'
    return teamSlug ? `/team/${teamSlug}` : explicitWorkspaceHref({ kind: 'personal' }, 'projects')
  }
  const surface = workspaceRoute?.surface
  if (surface === 'library' || surface === 'workflows') {
    // Stay on the surface you're using — the picked workspace's library or workflows.
    if (isPersonal) return explicitWorkspaceHref({ kind: 'personal' }, surface)
    return teamSlug
      ? explicitWorkspaceHref({ kind: 'team', slug: teamSlug }, surface)
      : explicitWorkspaceHref({ kind: 'personal' }, surface)
  }
  if (isPersonal) return explicitWorkspaceHref({ kind: 'personal' }, 'projects')
  return teamSlug
    ? explicitWorkspaceHref({ kind: 'team', slug: teamSlug }, 'projects')
    : explicitWorkspaceHref({ kind: 'personal' }, 'projects')
}
